import { useSyncExternalStore, useCallback } from 'react';

export type UrlPatch = Record<string, string | string[] | null | undefined>;

let stateSnapshot = new URLSearchParams();

const listeners = new Set<() => void>();

function notifyListeners() {
	listeners.forEach(listener => listener());
}

function getSnapshot(): URLSearchParams {
	return stateSnapshot;
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function resetOverviewState() {
	stateSnapshot = new URLSearchParams();
	notifyListeners();
}

export function useOverviewState() {
	const snapshot = useSyncExternalStore(subscribe, getSnapshot);

	const get = useCallback((key: string): string | null => snapshot.get(key), [snapshot]);

	const getAll = useCallback((key: string): string[] => snapshot.getAll(key), [snapshot]);

	const setMany = useCallback(
		(patch: UrlPatch) => {
			const next = new URLSearchParams(stateSnapshot);
			Object.entries(patch).forEach(([key, value]) => {
				next.delete(key);
				const values = Array.isArray(value) ? value : value ? [value] : [];
				values.forEach(v => next.append(key, v));
			});
			stateSnapshot = next;
			notifyListeners();
		},
		[]
	);

	const clear = useCallback(
		(keys: string[]) => setMany(Object.fromEntries(keys.map(key => [key, null]))),
		[setMany]
	);

	return { get, getAll, setMany, clear };
}
